import { Box, Flex, Text } from '@chakra-ui/react';
import { useContext } from 'react';
import {
  faEdit,
  faExclamationCircle,
  faInfoCircle,
  faSparkles
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  ButtonIcon,
  ButtonText,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast
} from '@morressier/alembic';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import {
  IGetSubmissionChangeRequestResponse,
  IGetSubmissionWorkflowResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useSubmitChangeRequest } from 'src/api/commands';
import { IFormFieldState } from 'src/components/FormFieldSet';
import { ISubmissionDomainActionReference } from '@publishing-engine/domain/actions/v1/commands';
import ProtectedWorkflowAction from 'src/components/ProtectedAction/ProtectedWorkflowAction';
import { changeRequestAllowedFields } from 'src/constants';
import { getAdjudicationRequestContextFromRoute } from 'src/contexts/AdjudicationDataProvider';
import { getAssessmentRequestContextFromRoute } from 'src/contexts/AssessmentDataProvider';
import { getReviewRequestContextFromRoute } from 'src/contexts/ReviewDataProvider';
import {
  getSubmissionRequestContextFromRoute,
  useSubmissionData
} from 'src/contexts/SubmissionDataProvider';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { commandWithSubTypes } from 'src/hooks/useFormFieldSet';
import {
  CommandName,
  useSubmissionFields
} from 'src/hooks/useSubmissionFields';
import { useSubmissionId } from 'src/hooks/useSubmissionId';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { EntryPoint } from 'src/utils/types';
import { Tooltip } from '@chakra-ui/tooltip';
import { FieldActionVisibilityContext } from 'src/contexts/FieldActionVisibilityContext';

interface Props {
  name: FormFieldDefinition['name'];
  label: string;
  onShowDiffModal: () => void;
  editCallback?: () => void;
  isUpdated: boolean;
  subtitle?: string;
  availableActions?: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  sectionHeadingTextStyle?: string;
  uploadCtaCopy?: string;
  isUpdateRequired?: boolean;
  tooltipText?: string;
  fileCount?: number;
}

interface AvailableActionsProps {
  fieldName: FormFieldDefinition['name'];
  label: string;
  commandName: ISubmissionDomainActionReference;
  isEditorialChangeRequestAvailable?: boolean;
  isEditorialEditSubmissionAvailable?: boolean;
  editCallback?: () => void;
  hasChangesRequested: boolean;
  entryPoint: EntryPoint;
  uploadCtaCopy?: string;
}

export const AvailableActions = ({
  fieldName,
  label,
  commandName,
  isEditorialChangeRequestAvailable,
  isEditorialEditSubmissionAvailable,
  editCallback,
  hasChangesRequested,
  entryPoint,
  uploadCtaCopy
}: AvailableActionsProps) => {
  const isFieldAllowedInChangeRequest =
    changeRequestAllowedFields.includes(fieldName);

  const { roleContext } =
    entryPoint === 'submission'
      ? getSubmissionRequestContextFromRoute()
      : entryPoint === 'early-assessment'
      ? getAssessmentRequestContextFromRoute()
      : entryPoint === 'adjudication'
      ? getAdjudicationRequestContextFromRoute()
      : getReviewRequestContextFromRoute();

  const { organizationId } = useWorkflow(entryPoint);

  if (isEditorialChangeRequestAvailable && isFieldAllowedInChangeRequest) {
    const toast = useToast();

    // Note: No problem in fetching these properties as this actions is checked by role.
    const ctxId = useCurrentContextId() ?? '';
    const submissionId = useSubmissionId() ?? '';

    const changeRequestMutation = useSubmitChangeRequest({
      submissionId,
      organizationId,
      submissionContextId: ctxId,
      eventSubType: 'fields',
      roleContext: 'editorial'
    });

    const submitChangeRequest = (fields: string[]) => {
      if (!fields.length) return undefined;
      return () => {
        changeRequestMutation.mutateAsync({ fields }).then(({ isSuccess }) => {
          if (isSuccess) {
            toast({
              variant: 'success',
              title: `${label} Update Request Logged`,
              description:
                'Send your request to the Author via "Request Submission Updates."',
              isClosable: true
            });
          } else {
            toast({
              variant: 'error',
              title: `${label} Update Request Logging failed.`,
              description: 'Try again after sometime.',
              isClosable: true
            });
          }
        });
      };
    };

    const actions = [
      {
        name: `Request Author Update for ${label}`,
        description: `This update will be logged along with any other requests – you can elaborate on the request and send it via 
          "Request Submission Updates."`,
        action: submitChangeRequest([fieldName]),
        isDisabled: hasChangesRequested
      }
    ];

    if (isEditorialEditSubmissionAvailable && editCallback) {
      actions.unshift({
        name: `Edit ${label}`,
        description: '',
        action: editCallback,
        isDisabled: false
      });
    }

    return (
      <Box position="relative" display="inline-block">
        <Menu ariaLabel="change request menu" placement="bottom">
          <ProtectedWorkflowAction actionName={commandName}>
            <MenuButton as={ButtonText} variant="secondary">
              <Flex gap={2} alignItems="center">
                <FontAwesomeIcon icon={faEdit} />
                <Text>Actions</Text>
              </Flex>
            </MenuButton>
          </ProtectedWorkflowAction>
          <MenuList
            // TODO: Resolve it later, temporary fix
            // currently its displaying the menu on top left screen corner
            marginTop={8}
            padding={2}
            width="368px"
          >
            <Flex flexDirection="column" gap={4} position="relative">
              {actions.map(action => (
                <MenuItem
                  key={action.name}
                  ariaLabel={action.name}
                  // TODO: Currently we don't have MenuItem with disabled state
                  // that is why we are using opacity to show disabled state
                  onClick={
                    action.action && !action.isDisabled
                      ? action.action
                      : () => {}
                  }
                >
                  <Flex flexDirection="column" gap={1}>
                    <Text
                      textStyle="headingLine"
                      style={action.isDisabled ? { opacity: 0.6 } : {}}
                    >
                      {action.name}
                    </Text>
                    {action.description && (
                      <Text
                        textStyle="headingOverline"
                        style={action.isDisabled ? { opacity: 0.6 } : {}}
                      >
                        {action.description}
                      </Text>
                    )}
                  </Flex>
                </MenuItem>
              ))}
            </Flex>
          </MenuList>
        </Menu>
      </Box>
    );
  }
  return (
    ['author', 'editorial'].includes(roleContext) && (
      <ProtectedWorkflowAction actionName={commandName}>
        <ButtonText
          iconLeft={<FontAwesomeIcon icon={faEdit} />}
          isDisabled={!editCallback}
          onClick={editCallback}
        >
          {uploadCtaCopy ? uploadCtaCopy : 'Update'}
        </ButtonText>
      </ProtectedWorkflowAction>
    )
  );
};

export const editSubmissionUpdateStatus = (onShowDiffModal?: () => void) => {
  return (
    <Flex textStyle="textBody" gap={2} alignItems="center">
      {onShowDiffModal && (
        <ButtonText onClick={onShowDiffModal}>View Updates</ButtonText>
      )}
      <Badge variant="attention" size="context">
        <Flex gap={1} alignItems="center">
          <FontAwesomeIcon icon={faSparkles} />
          Updated
        </Flex>
      </Badge>
    </Flex>
  );
};

export const FieldHeader = ({
  name,
  label,
  editCallback,
  isUpdated,
  subtitle,
  onShowDiffModal,
  availableActions,
  entryPoint,
  sectionHeadingTextStyle,
  uploadCtaCopy,
  isUpdateRequired,
  tooltipText,
  fileCount
}: Props) => {
  const hideFieldActions = useContext(FieldActionVisibilityContext);
  const { changeRequestData, workflowResponse } =
    entryPoint === 'submission'
      ? useSubmissionData()
      : ({
          changeRequestData: {},
          workflowResponse: {}
        } as {
          changeRequestData?: IGetSubmissionChangeRequestResponse;
          workflowResponse?: IGetSubmissionWorkflowResponse;
        });

  const { getFieldState } = useSubmissionFields(entryPoint);

  const isEditorialChangeRequestAvailable =
    availableActions?.contains('EditorialSubmitChangeRequest#fields', true) ||
    availableActions?.contains('EditorialSubmitChangeRequest#form', true);

  const isEditorialEditSubmissionAvailable =
    availableActions?.contains('EditSubmission', true) ||
    availableActions?.contains('ChangeArticleType', true);

  const isAuthorChangeRequestAvailable =
    availableActions?.contains('AuthorCompleteChangeRequest#fields', true) ||
    availableActions?.contains('AuthorCompleteChangeRequest#form', true);

  const updateStatus = isAuthorChangeRequestAvailable
    ? editSubmissionUpdateStatus()
    : editSubmissionUpdateStatus(onShowDiffModal);

  // changes requested for author
  const hasAuthorChangeRequests =
    changeRequestData?.changeRequest?.fields?.includes(name) ?? false;

  const pendingChangeRequestFields =
    changeRequestData?.changeRequest?.fields ?? [];
  const submittedChangeRequestFields = (
    changeRequestData?.changeRequest?.requestHistory ?? []
  ).flatMap(history => history.fields || []);

  const allChangeRequestFields = Array.from(
    new Set([...pendingChangeRequestFields, ...submittedChangeRequestFields])
  );

  // changes requested by editor
  const hasEditorRequestedChanges =
    allChangeRequestFields.includes(name) ?? false;

  const showRequestedChangeToAuthor =
    hasAuthorChangeRequests && isAuthorChangeRequestAvailable;
  const showRequestedChangeToEditor =
    hasEditorRequestedChanges && isEditorialChangeRequestAvailable;

  // get field editing for protected action workflow
  const field = getFieldState(name) as IFormFieldState;
  const editViaCommand = field?.editing?.via as CommandName;
  const commandName = commandWithSubTypes[editViaCommand];
  const enabledPhase = ['revision', 'immediateRevision'].includes(
    workflowResponse?.workflowData?.currentState ?? ''
  );

  const displayLabel = fileCount !== undefined ? `${label} (${fileCount})` : label;

  return (
    <Flex flexDirection={'column'}>
      <Flex flexDirection={'row'} justifyContent="space-between" gap={2} flexWrap="wrap">
        <Flex alignItems="flex-start" gap={2} flexDirection="row" width={{ base: '100%', md: 'auto' }}>
          <Text
            id={label}
            textStyle={sectionHeadingTextStyle || 'headingItem'}
            textTransform={'none'}
          >
            {displayLabel}
            {field.formFieldDefinition.params.validations.required && label && (
              <Box as="span" color="actionSecondaryCautionContent">
                *
              </Box>
            )}
            {/* while revision submission Corresponding Author required tooltip */}
            {tooltipText && (
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label={tooltipText}
                borderRadius={4}
                data-cy="info-tooltip"
              >
                <ButtonIcon
                  size="small"
                  ariaLabel="field information"
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  data-cy="info-icon"
                  ml={1}
                />
              </Tooltip>
            )}
          </Text>
          {!hideFieldActions &&
            (!enabledPhase || (isUpdateRequired && enabledPhase)) && (
              <AvailableActions
                fieldName={name}
                label={label}
                commandName={commandName}
                isEditorialChangeRequestAvailable={
                  isEditorialChangeRequestAvailable
                }
                isEditorialEditSubmissionAvailable={
                  isEditorialEditSubmissionAvailable
                }
                editCallback={editCallback}
                hasChangesRequested={hasEditorRequestedChanges}
                entryPoint={entryPoint}
                uploadCtaCopy={uploadCtaCopy}
              />
            )}
        </Flex>

        {isUpdated && updateStatus}

        {(showRequestedChangeToAuthor || showRequestedChangeToEditor) &&
          !isUpdated && (
            <Flex textStyle="textBody" gap={2} alignItems="center">
              <Badge variant="attention" size="context">
                <Flex gap={1} alignItems="center">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  Changes Requested
                </Flex>
              </Badge>
            </Flex>
          )}
      </Flex>

      {editCallback && subtitle ? <Text>{subtitle}</Text> : null}
    </Flex>
  );
};
